import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Mail, Lock, ShieldCheck, Zap, Ruler, Award, Radio, Search, Cpu, Handshake, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { Input, Button, Label, BlueprintBg } from "../components/Primitives";
import { Logo, LangToggle } from "../components/AppShell";
import { networkStats } from "../data/mockData";

const TRUST_ITEMS = [
  { icon: ShieldCheck, key: "trustA" },
  { icon: Zap, key: "trustB" },
  { icon: Ruler, key: "trustC" },
  { icon: Award, key: "trustD" },
];
const FLOW_STEPS = [
  { icon: Search, key: "flow1", n: "01" },
  { icon: Cpu, key: "flow2", n: "02" },
  { icon: Handshake, key: "flow3", n: "03" },
];

export default function Login() {
  const { t } = useI18n();
  const { signIn, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    const { error } = await signIn({ email, password, remember });
    setBusy(false);
    if (error) { toast.error(error.message || "Login failed"); return; }
    navigate("/app/dashboard");
  };

  const forgot = async () => {
    if (!email) { toast.error("Enter your email first"); return; }
    const { error } = await resetPassword(email);
    if (error) toast.error(error.message);
    else toast.success("Check your inbox for the reset link");
  };

  return (
    <div className="min-h-screen bg-carbon-900 text-ink-900 flex flex-col relative overflow-hidden carbon-grain">
      <BlueprintBg opacity={0.55} />
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage:
          "radial-gradient(ellipse 60% 45% at 12% 8%, rgba(46,107,255,0.10), transparent 60%), " +
          "radial-gradient(ellipse 50% 40% at 92% 96%, rgba(46,107,255,0.06), transparent 60%)"
      }} />
      <header className="relative z-10 flex items-center justify-between px-6 md:px-10 py-5 border-b border-carbon-600">
        <Logo size="lg" />
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-ink-500">
            <Radio className="w-3 h-3 text-status-available animate-pulse" />
            <span>{t("login.status")}</span>
          </div>
          <LangToggle />
        </div>
      </header>

      <main className="relative z-10 flex-1 grid lg:grid-cols-[1.05fr_1fr] gap-10 xl:gap-16 max-w-7xl mx-auto w-full px-6 md:px-10 py-10 lg:py-14">
        <section className="flex flex-col justify-center order-2 lg:order-1">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-electric-600 mb-4">{t("login.kicker")}</p>
          <h1 className="text-4xl sm:text-5xl xl:text-[3.4rem] font-semibold tracking-tight leading-[1.05]">{t("login.welcome")}</h1>
          <p className="text-ink-700 mt-4 text-base leading-relaxed max-w-md">{t("login.subtitle")}</p>

          <div className="mt-8">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink-500 mb-3">{t("login.flowTitle")}</p>
            <div className="space-y-2">
              {FLOW_STEPS.map(({ icon: Icon, key, n }, i) => (
                <div key={key} className="group flex items-center gap-3">
                  <span className="font-mono text-[10px] text-electric-600 w-6">{n}</span>
                  <div className="w-8 h-8 rounded-md bg-carbon-800 border border-carbon-600 flex items-center justify-center group-hover:border-electric-500/50 transition">
                    <Icon className="w-3.5 h-3.5 text-electric-600" />
                  </div>
                  <span className="text-sm text-ink-800">{t(`login.${key}`)}</span>
                  {i < FLOW_STEPS.length - 1 && <span className="hidden sm:inline-block flex-1 divider-glow ml-2" />}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {TRUST_ITEMS.map(({ icon: Icon, key }) => (
              <div key={key} className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-carbon-800 border border-carbon-600">
                <Icon className="w-3.5 h-3.5 text-electric-600 shrink-0" />
                <span className="text-[11px] text-ink-700 truncate">{t(`login.${key}`)}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-baseline gap-5 text-xs text-ink-500">
            <div><span className="font-mono text-ink-900 text-base font-semibold">{networkStats.producers.toLocaleString()}</span> <span>producers</span></div>
            <span>·</span>
            <div><span className="font-mono text-ink-900 text-base font-semibold">{networkStats.avgMatchSeconds}s</span> <span>avg match</span></div>
            <span>·</span>
            <div><span className="font-mono text-ink-900 text-base font-semibold">{networkStats.onTimeAvg}%</span> <span>on-time</span></div>
          </div>
        </section>

        <section className="flex items-center justify-center order-1 lg:order-2">
          <div className="w-full max-w-md relative">
            <div className="absolute -top-3 -left-3 w-6 h-6 border-t border-l border-electric-500/50" />
            <div className="absolute -top-3 -right-3 w-6 h-6 border-t border-r border-electric-500/50" />
            <div className="absolute -bottom-3 -left-3 w-6 h-6 border-b border-l border-electric-500/50" />
            <div className="absolute -bottom-3 -right-3 w-6 h-6 border-b border-r border-electric-500/50" />

            <form onSubmit={submit} className="bg-carbon-800 backdrop-blur-xl border border-carbon-600 rounded-2xl p-7 space-y-5 card-soft" data-testid="login-form">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[10px] uppercase tracking-widest text-ink-500">R4Pro · Sign-in</p>
                <span className="font-mono text-[10px] uppercase tracking-widest text-electric-600">SESSION · SEC</span>
              </div>

              <div>
                <Label>{t("login.email")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
                  <Input type="email" autoFocus autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10" data-testid="login-email-input" required />
                </div>
              </div>

              <div>
                <Label>{t("login.password")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
                  <Input type={showPass ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 pr-10" data-testid="login-password-input" required />
                  <button type="button" onClick={() => setShowPass((v) => !v)} data-testid="login-toggle-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-800"
                    aria-label={showPass ? "Hide password" : "Show password"}
                  >
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs">
                <label className="flex items-center gap-2 text-ink-700 cursor-pointer">
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-electric-500" data-testid="login-remember" />
                  {t("login.remember")}
                </label>
                <button type="button" onClick={forgot} data-testid="login-forgot" className="text-electric-600 hover:text-electric-500">{t("login.forgot")}</button>
              </div>

              <Button type="submit" className="w-full h-11" data-testid="login-submit-btn" disabled={busy}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("login.signIn")} <ArrowRight className="w-4 h-4" /></>}
              </Button>

              <p className="text-center text-xs text-ink-500">
                {t("login.noAccount")}{" "}
                <button type="button" onClick={() => navigate("/register")} data-testid="login-go-register" className="text-electric-600 hover:text-electric-500 font-medium">{t("login.register")}</button>
              </p>
            </form>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-carbon-600 px-6 md:px-10 py-4 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-ink-500">
        <span>{t("brand.tagline")}</span>
        <span>R4Pro · Manufacturing Operating System</span>
      </footer>
    </div>
  );
}
